import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type CostEstimate,
  clientCostEstimateShowQueryKey,
  type SelfPayCostEstimateInput,
  useClientCostEstimateCreateMutation,
  useClientCostEstimateUpdateMutation,
} from '@cartwheel/api/v1'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../button'

interface SelfPaidEstimateFormProps {
  clientId: string
  estimate: CostEstimate | null
  // Localized text the server will write as custom_message when this is saved
  // (computed in the family's locale). Always supplied by the parent so the
  // preview matches the persist path even before the user clicks Save.
  previewMessage: string
  onSaved: () => void
  onCancel: () => void
}

export function SelfPaidEstimateForm({
  clientId,
  estimate,
  previewMessage,
  onSaved,
  onCancel,
}: SelfPaidEstimateFormProps) {
  const localizedMessage = estimate?.custom_message ?? previewMessage
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: clientCostEstimateShowQueryKey({ path: { client_id: clientId } }),
    })

  const createMutation = useClientCostEstimateCreateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })
  const updateMutation = useClientCostEstimateUpdateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setErrorMessage(null)

    const body: SelfPayCostEstimateInput = { estimate_type: 'self_pay' }

    if (estimate?.id) {
      updateMutation.mutate({ path: { client_id: clientId, id: estimate.id }, body })
    } else {
      createMutation.mutate({ path: { client_id: clientId }, body })
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex justify-between items-center py-2 px-4 border-b border-t border-gray-stroke">
        <span className="font-medium">Self-Paid</span>
        <span className="text-2xl font-ginto-nord-md">$120-145</span>
      </div>

      <div className="my-6 grid gap-4">
        <h3 className="font-ginto-nord-md text-charcoal text-lg font-semibold">
          Message to family
        </h3>
        <div className="p-4 bg-sand-25 rounded border border-gray-stroke text-charcoal">
          {localizedMessage}
        </div>
      </div>

      {errorMessage && <div className="mt-2 text-red-700 text-sm text-right">{errorMessage}</div>}

      <div className="flex justify-end gap-2 mt-10">
        <Button type="button" variant="tertiary" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}
